import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import playbookService from '../services/playbookService';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import PageHeader from '../components/common/PageHeader';

function PlaybookList() {
  const { t } = useLanguage();
  const { darkMode } = useTheme();
  const [playbooks, setPlaybooks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState({
    category: '',
    platform: '',
    search: ''
  });

  const fetchPlaybooks = useCallback(async () => {
    try {
      setLoading(true);
      const [playbooksData, categoriesData] = await Promise.all([
        playbookService.getPlaybooks(filter),
        playbookService.getCategories()
      ]);
      setPlaybooks(Array.isArray(playbooksData) ? playbooksData : []);
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchPlaybooks();
  }, [fetchPlaybooks]);

  const handleDelete = async (id) => {
    if (window.confirm(t('playbooks.confirmDelete'))) {
      try {
        await playbookService.deletePlaybook(id);
        fetchPlaybooks();
      } catch (err) {
        setError(err.message);
      }
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await playbookService.duplicatePlaybook(id);
      fetchPlaybooks();
    } catch (err) {
      setError(err.message);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      reconnaissance: darkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800',
      initial_access: darkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800',
      execution: darkMode ? 'bg-orange-900 text-orange-300' : 'bg-orange-100 text-orange-800',
      persistence: darkMode ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-800',
      privilege_escalation: darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800',
      defense_evasion: darkMode ? 'bg-teal-900 text-teal-300' : 'bg-teal-100 text-teal-800',
      credential_access: darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800',
      discovery: darkMode ? 'bg-indigo-900 text-indigo-300' : 'bg-indigo-100 text-indigo-800',
      lateral_movement: darkMode ? 'bg-pink-900 text-pink-300' : 'bg-pink-100 text-pink-800',
      collection: darkMode ? 'bg-rose-900 text-rose-300' : 'bg-rose-100 text-rose-800',
      exfiltration: darkMode ? 'bg-amber-900 text-amber-300' : 'bg-amber-100 text-amber-800',
      impact: darkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800',
      custom: darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
    };
    return colors[category] || colors.custom;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-red-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <PageHeader title={t('playbooks.title')} subtitle={t('playbooks.subtitle')}>
        <Link
          to="/playbooks/executions"
          className={`px-4 py-2 rounded-lg ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
        >
          {t('playbooks.viewExecutions')}
        </Link>
        <Link
          to="/playbooks/new"
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          {t('playbooks.newPlaybook')}
        </Link>
      </PageHeader>

      {error && (
        <div className={`px-4 py-3 rounded mb-4 ${darkMode ? 'bg-red-900/50 border border-red-500 text-red-200' : 'bg-red-100 border border-red-400 text-red-700'}`}>
          {error}
        </div>
      )}

      {/* Category Pills */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setFilter({ ...filter, category: '' })}
          className={`px-3 py-1 rounded-full text-sm ${
            filter.category === '' ? 'bg-red-600 text-white' : darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          All ({playbooks.length})
        </button>
        {categories.map((cat) => (
          <button
            key={cat._id}
            onClick={() => setFilter({ ...filter, category: cat._id })}
            className={`px-3 py-1 rounded-full text-sm ${
              filter.category === cat._id ? 'bg-red-600 text-white' : getCategoryColor(cat._id)
            }`}
          >
            {cat._id?.replace('_', ' ') || 'Unknown'} ({cat.count})
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className={`rounded-lg p-4 mb-6 ${darkMode ? 'bg-gray-800' : 'bg-white shadow'}`}>
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Search playbooks..."
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              className={`w-full px-3 py-2 rounded border ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
            />
          </div>
          <select
            value={filter.platform}
            onChange={(e) => setFilter({ ...filter, platform: e.target.value })}
            className={`px-3 py-2 rounded border ${darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
          >
            <option value="">All Platforms</option>
            {playbookService.PLATFORMS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          <button
            onClick={() => setFilter({ category: '', platform: '', search: '' })}
            className={`px-3 py-2 ${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Playbooks Grid */}
      {playbooks.length === 0 ? (
        <div className={`rounded-lg p-8 text-center ${darkMode ? 'bg-gray-800' : 'bg-white shadow'}`}>
          <p className={`mb-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('playbooks.noPlaybooks')}</p>
          <Link
            to="/playbooks/new"
            className="inline-block bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
          >
            {t('playbooks.createPlaybook')}
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {playbooks.map((playbook) => (
            <div key={playbook._id} className={`rounded-lg p-4 transition-colors ${darkMode ? 'bg-gray-800 hover:bg-gray-750' : 'bg-white shadow hover:shadow-md'}`}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <Link to={`/playbooks/${playbook._id}`} className={`font-medium hover:text-red-500 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {playbook.name}
                  </Link>
                  <span className={`ml-2 px-2 py-0.5 rounded text-xs ${getCategoryColor(playbook.category)}`}>
                    {playbook.category?.replace('_', ' ')}
                  </span>
                </div>
                {playbook.isTemplate && (
                  <span className={`px-2 py-0.5 rounded text-xs ${darkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800'}`}>
                    Template
                  </span>
                )}
              </div>

              <p className={`text-sm mb-3 line-clamp-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {playbook.description || 'No description'}
              </p>

              <div className="flex flex-wrap gap-2 mb-3">
                {playbook.targetPlatforms?.map((platform) => (
                  <span key={platform} className={`px-2 py-0.5 rounded text-xs ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
                    {platform === 'darwin' ? 'macOS' : platform}
                  </span>
                ))}
              </div>

              <div className={`flex items-center justify-between text-sm mb-3 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                <span>{playbook.steps?.length || 0} steps</span>
                <span>{playbook.executionCount || 0} executions</span>
              </div>

              {/* MITRE Techniques */}
              {playbook.mitreTechniques?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {playbook.mitreTechniques.slice(0, 3).map((tech) => (
                    <span key={tech.techniqueId} className={`px-1.5 py-0.5 rounded text-xs ${darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700'}`}>
                      {tech.techniqueId}
                    </span>
                  ))}
                  {playbook.mitreTechniques.length > 3 && (
                    <span className="text-gray-500 text-xs">
                      +{playbook.mitreTechniques.length - 3} more
                    </span>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className={`flex gap-2 pt-3 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <Link
                  to={`/playbooks/${playbook._id}`}
                  className={`flex-1 text-center py-1.5 rounded text-sm ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                >
                  View
                </Link>
                <Link
                  to={`/playbooks/${playbook._id}/execute`}
                  className="flex-1 text-center bg-red-600 hover:bg-red-700 text-white py-1.5 rounded text-sm"
                >
                  Execute
                </Link>
                <button
                  onClick={() => handleDuplicate(playbook._id)}
                  className={`px-3 py-1.5 rounded text-sm ${darkMode ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                  title="Duplicate"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z" />
                    <path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z" />
                  </svg>
                </button>
                <button
                  onClick={() => handleDelete(playbook._id)}
                  className={`px-3 py-1.5 rounded text-sm ${darkMode ? 'bg-gray-700 hover:bg-red-600 text-white' : 'bg-gray-200 hover:bg-red-500 hover:text-white text-gray-700'}`}
                  title="Delete"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default PlaybookList;
